import { ReactNode } from "react";

type MaxWidth = "sm" | "md" | "lg" | "xl" | "full";

interface PageLayoutProps {
  title?: string;
  description?: string;
  actions?: ReactNode;
  tabs?: ReactNode;
  sidebar?: ReactNode;
  maxWidth?: MaxWidth;
  legalPadBg?: boolean;
  children: ReactNode;
}

const maxWidthStyles: Record<MaxWidth, string> = {
  sm: "max-w-2xl",
  md: "max-w-4xl",
  lg: "max-w-6xl",
  xl: "max-w-7xl",
  full: "max-w-full",
};

export function PageLayout({
  title,
  description,
  actions,
  tabs,
  sidebar,
  maxWidth = "xl",
  legalPadBg = false,
  children,
}: PageLayoutProps) {
  return (
    <div
      className={`min-h-screen ${legalPadBg ? "legal-pad-bg" : "bg-[var(--color-bg-primary)]"}`}
    >
      <div className={`mx-auto px-4 py-8 ${maxWidthStyles[maxWidth]}`}>
        {/* Page Header */}
        {(title || actions) && (
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="flex-1">
              {title && (
                <h1 className="text-3xl font-bold text-neutral-900 keep-all">
                  {title}
                </h1>
              )}
              {description && (
                <p className="mt-2 text-neutral-600 keep-all">{description}</p>
              )}
            </div>
            {actions && (
              <div className="flex items-center gap-2">{actions}</div>
            )}
          </div>
        )}

        {/* Tabs */}
        {tabs && <div className="mb-6">{tabs}</div>}

        {/* Content with optional sidebar */}
        <div className="flex gap-6">
          <div className="flex-1">{children}</div>
          {sidebar && (
            <aside className="hidden lg:block w-64 flex-shrink-0">
              {sidebar}
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}
